import { callFunction } from '../firebase/FunctionsService';

export const AUDIT_PURGE_CONFIRM = 'PURGE_AUDIT_EVENTS';

type AuditExportResult = {
  ok: boolean;
  exportedAt: string;
  events: Record<string, unknown>[];
  count: number;
  nextCursor: string | null;
  truncated: boolean;
};

type AuditPurgeResult = {
  ok: boolean;
  deleted: number;
};

function triggerJsonDownload(filename: string, payload: unknown) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Fetch all reachable audit pages and download a JSON file. */
export async function exportAndDownloadAuditEvents(opts?: {
  maxEventsPerPage?: number;
  maxPages?: number;
}): Promise<{ count: number; truncated: boolean; filename: string }> {
  const maxEventsPerPage = opts?.maxEventsPerPage ?? 2000;
  const maxPages = opts?.maxPages ?? 10;
  const all: Record<string, unknown>[] = [];
  let cursor: string | null = null;
  let truncated = false;
  let exportedAt = new Date().toISOString();

  for (let page = 0; page < maxPages; page += 1) {
    const result = await callFunction('exportAuditEvents', {
      cursor,
      maxEvents: maxEventsPerPage,
    }) as AuditExportResult;
    exportedAt = result.exportedAt || exportedAt;
    all.push(...(result.events ?? []));
    cursor = result.nextCursor;
    truncated = Boolean(result.truncated);
    if (!cursor) {
      truncated = false;
      break;
    }
  }

  const stamp = exportedAt.replace(/[:.]/g, '-');
  const filename = `alea-audit-export-${stamp}.json`;
  triggerJsonDownload(filename, {
    exportedAt,
    count: all.length,
    truncated,
    events: all,
  });

  return { count: all.length, truncated, filename };
}

export async function purgeAuditEvents(): Promise<AuditPurgeResult> {
  return callFunction('purgeAuditEvents', {
    confirm: AUDIT_PURGE_CONFIRM,
  }) as Promise<AuditPurgeResult>;
}
